import { DeleteOutlined, EditOutlined, EyeOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Col, Row, Typography } from "antd";
import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { useSmoothLoading } from "../../../../../../hooks/useSmoothLoading";
import { GridType, ICellRendererParams } from "../../../../../../types/grid";
import { getPresignedImage } from "../../../../../../utils/media";
import Grid from "../../../../../atoms/Grid";
import { Popup } from "../../../../../atoms/Popup";
import Loader from "../../../../../modules/Loader.tsx";
import { Vehicle, Vehicles } from "./types";
import { useVehicleDetailModal } from "./useVehicleDetailModal";

const getVehicleSlots = (noOfParkingSlots: number | undefined, vehicles: Vehicle[] | undefined) => {
  const totalSlots = noOfParkingSlots ?? 0;
  const totalVehicles = vehicles?.length ?? 0;
  const availableSlots = Math.max(totalSlots - totalVehicles, 0);
  const isAddButtonVisible = availableSlots > 0;

  return { availableSlots, isAddButtonVisible };
};

export const VehicleDetails = () => {
  const { isLoading, startLoading, stopLoading } = useSmoothLoading(500);
  const tenantData = useSelector((state: any) => state.tenantsSummary?.tenantData);

  const { noOfParkingSlots = 0, vehicles = [] } = (tenantData?.vehicleDetail as Vehicles) ?? {};

  useEffect(() => {
    if (!tenantData) startLoading();
    else stopLoading();
  }, [tenantData, startLoading, stopLoading]);

  const { availableSlots, isAddButtonVisible } = getVehicleSlots(noOfParkingSlots, vehicles);

  const { isOpen, formType, popupContent, handleOpen, closeModal } = useVehicleDetailModal({
    userId: tenantData?.userDetail?.userId,
    vehicles,
  });

  const columns: GridType[] = [
    {
      field: "typeOfVehicle",
      headerName: "Type",
      flex: 1,
      minWidth: 250,
    },
    { field: "brand", headerName: "Brand", flex: 1, minWidth: 250 },
    { field: "model", headerName: "Model", flex: 1, minWidth: 250 },
    { field: "color", headerName: "Color", flex: 1, minWidth: 250 },
    { field: "plateCode", headerName: "Plate Code", flex: 1, minWidth: 120 },
    { field: "plateNumber", headerName: "Plate Number", flex: 1, minWidth: 140 },
    { field: "city", headerName: "City", flex: 1, minWidth: 250 },
    {
      field: "documentId",
      headerName: "Document",
      flex: 0.4,
      minWidth: 120,
      cellRenderer: (props: ICellRendererParams<Vehicle>) => {
        if (!props.data.documentId) return;
        return (
          <Button
            type="text"
            size="small"
            icon={<EyeOutlined />}
            onClick={async () => {
              const response = await getPresignedImage([props.data.documentId]);
              window.open(response[0], "_blank");
            }}
          >
            View
          </Button>
        );
      },
    },
    {
      field: "action",
      headerName: "Action",
      flex: 0.4,
      minWidth: 100,
      cellRenderer: (props: ICellRendererParams<Vehicle>) => (
        <div className="d-flex align-items-center">
          <Button type="text" size="small" onClick={() => handleOpen("edit", props.data)}>
            <EditOutlined />
          </Button>
          <Button type="text" size="small" onClick={() => handleOpen("delete", props.data)}>
            <DeleteOutlined />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <Col>
      <Row justify="space-between" align="middle" className="mb-1">
        <Typography.Title className="m-0" level={2}>
          Vehicle Details
        </Typography.Title>
        <div>
          <span className="mr-5">{`Available parking slots: ${availableSlots}`}</span>
          {isAddButtonVisible && (
            <Button size="large" type="primary" className="ef-approve-btn" onClick={() => handleOpen("add")}>
              Add New Vehicle <PlusOutlined />
            </Button>
          )}
        </div>
      </Row>

      <Row>{isLoading ? <Loader /> : <Grid className="p-0" columns={columns} data={vehicles} fullPage />}</Row>

      <Popup
        closable
        visibility={isOpen}
        title={
          <Typography.Title level={3} className="m-0">
            {popupContent[formType].title}
          </Typography.Title>
        }
        content={popupContent[formType].content}
        width={850}
        style={{ padding: "10px" }}
        onCancel={closeModal}
      />
    </Col>
  );
};
